import { useCallback, useEffect, useRef, useState } from 'react'

export const PEEK_OPEN_DELAY_MS = 120
export const PEEK_CLOSE_DELAY_MS = 200

const isKeyboardFocus = target => {
  try {
    return target.matches(':focus-visible')
  } catch (e) {
    return false
  }
}

/**
 * Lets a collapsed nav rail expand temporarily while the pointer is over it or
 * keyboard focus is inside it. Open/close are debounced so a pointer crossing
 * the rail does not flash it open.
 *
 * `dismiss()` collapses the peek right away and keeps it closed until the
 * pointer and focus have both left the nav, so that explicitly collapsing the
 * nav with the pointer still over it does not immediately reopen it.
 */
export default function useNavPeek({ enabled }) {
  const [isPeeking, setIsPeeking] = useState(false)
  const timer = useRef(null)
  const hovered = useRef(false)
  const focused = useRef(false)
  const dismissed = useRef(false)

  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }, [])

  const schedule = useCallback(
    shouldPeek => {
      clearTimer()
      timer.current = setTimeout(
        () => {
          timer.current = null
          setIsPeeking(shouldPeek)
        },
        shouldPeek ? PEEK_OPEN_DELAY_MS : PEEK_CLOSE_DELAY_MS,
      )
    },
    [clearTimer],
  )

  const sync = useCallback(() => {
    if (!hovered.current && !focused.current) {
      dismissed.current = false
    }
    schedule((hovered.current || focused.current) && !dismissed.current)
  }, [schedule])

  const onMouseEnter = useCallback(() => {
    hovered.current = true
    sync()
  }, [sync])

  const onMouseLeave = useCallback(() => {
    hovered.current = false
    sync()
  }, [sync])

  const onFocus = useCallback(
    event => {
      if (!isKeyboardFocus(event.target)) return
      focused.current = true
      sync()
    },
    [sync],
  )

  const onBlur = useCallback(
    event => {
      if (event.currentTarget.contains(event.relatedTarget)) return
      focused.current = false
      sync()
    },
    [sync],
  )

  const dismiss = useCallback(() => {
    dismissed.current = true
    clearTimer()
    setIsPeeking(false)
  }, [clearTimer])

  useEffect(() => {
    if (!enabled) {
      hovered.current = false
      focused.current = false
      dismissed.current = false
      clearTimer()
      setIsPeeking(false)
    }
  }, [enabled, clearTimer])

  useEffect(() => clearTimer, [clearTimer])

  return {
    isPeeking: enabled && isPeeking,
    dismiss,
    peekHandlers: { onMouseEnter, onMouseLeave, onFocus, onBlur },
  }
}
